import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import NiceModal from "@ebay/nice-modal-react";
import { fetchData } from "../../Helpers";
import { startWebSession, trackWebEvent } from "../../api/web";
import { showErrorPopup, translateError } from "@components/ErrorHandling";
import SideBySideLayout from "@components/SideBySideLayout";
import Button from "@components/Button";
import Input from "@components/Input";
import Switch from "@components/Switch";
import BGMobile from "./img/bg-mobile.jpg";
import BGDesktop from "./img/bg-desktop.jpg";
import "./style.scss";

const CONSENT_LABEL = `Ich möchte das Prämienprogramm Inspiration Club nutzen. Ich willige ein, dass <a href="https://www.bat.de/" target="_blank" rel="noopener noreferrer"><strong>BAT</strong></a> Angebote des Inspiration Club an meine E-Mail-Adresse sendet und meine auf Webseiten von <a href="https://www.bat.de/" target="_blank" rel="noopener noreferrer"><strong>BAT</strong></a> erhobenen Daten (z.B. Einkäufe, erhaltene Prämien) zu einem individuellen webseitenübergreifenden Profil zum Zwecke von Geschäftsanalysen und Direktwerbung zusammenführt.`;

function ConsentView({ code, m2Token, onSuccess }) {
  const { register, handleSubmit, watch } = useForm();
  const [loading, setLoading] = useState(false);
  const consentChecked = watch("consent");

  function onSubmit() {
    const controller = new AbortController();
    const payload = {
      consumer: {
        consentIcDeOptin: true,
        consentIcDeEmail: true,
      },
    };

    fetchData({
      endpoint: "/consumer/login-join",
      payload,
      m2Token,
      successCallback: (resObj) => {
        localStorage.setItem("token", resObj.data.token.uuid);
        sessionStorage.setItem("loginSource", "login_form_join");
        onSuccess();
      },
      errorCallback: (resObj) => {
        showErrorPopup(translateError(resObj.data.error), resObj.meta.ts);
      },
      controller,
      setLoading,
      shouldThrowError: false,
    });
  }

  return (
    <form className="qr-landing__consent" onSubmit={handleSubmit(onSubmit)}>
      <div className="qr-landing__consent-body">
        <h1 className="qr-landing__consent-title">
          Du bist noch kein Mitglied im Inspiration Club.
        </h1>
        <div className="qr-landing__consent-content">
          <p className="qr-landing__consent-subtitle">
            Das lässt sich ganz einfach ändern! Stimme der Einwilligung zu und
            tritt dem Inspiration Club kostenlos bei!
          </p>
          <div className="qr-landing__consent-fields">
            <Switch
              name="consent"
              register={register}
              label={CONSENT_LABEL}
              validationRules={{ required: true }}
            />
            <p className="qr-landing__consent-notice">
              <strong>Hinweis:</strong> Du kannst jede der oben von Dir
              erteilten Einwilligungen jederzeit mit Wirkung für die Zukunft
              widerrufen. Weitere Informationen über unsere Datenverarbeitung
              findest Du in unseren{" "}
              <a
                href="https://www.discoverglo.com/de/de/datenschutz/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Datenschutzbestimmungen
              </a>
              .<br />
              <br />
              Für das Prämienprogramm Inspiration Club gelten diese{" "}
              <a
                href="https://api.de.inspirationclub.tdy-apps.com/terms/latest"
                target="_blank"
                rel="noopener noreferrer"
              >
                Teilnahmebedingungen
              </a>
              .
            </p>
          </div>
        </div>
      </div>
      <div className="qr-landing__consent-footer">
        <Button submit disabled={!consentChecked || loading} btnClass="ultra-insider">
          Mitglied Werden
        </Button>
      </div>
    </form>
  );
}

function LoginView({ code, onSuccess, onJoin }) {
  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields, isValid },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });
  const [loading, setLoading] = useState(false);

  function onSubmit(data) {
    const controller = new AbortController();
    const payload = {
      consumer: {
        emailAddress: data.emailAddress,
        password: data.pass,
      },
    };

    fetchData({
      endpoint: "/consumer/m2-login",
      payload,
      successCallback: (resObj) => {
        if (resObj.data.process === "join") {
          onJoin(resObj.data.token.uuid);
        } else {
          localStorage.setItem("token", resObj.data.token.uuid);
          sessionStorage.setItem("loginSource", "login_form");
          onSuccess();
        }
      },
      errorCallback: (resObj) => {
        showErrorPopup(translateError(resObj.data.error), resObj.meta.ts);
      },
      controller,
      setLoading,
      shouldThrowError: false,
    });
  }

  return (
    <form className="qr-landing__login" noValidate onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <div className="qr-landing__login-copy">
        <h1>Logge dich beim Inspiration Club ein</h1>
        <p>Gib deine Anmeldedaten ein, die du auch für die MYVUSE App verwendest.</p>
      </div>
      <div className="qr-landing__login-fields">
        <Input
          label="E-mail"
          name="emailAddress"
          type="email"
          placeholder="Gib deine E-Mail-Adresse ein"
          register={register}
          errors={errors.emailAddress}
          touched={!!touchedFields.emailAddress}
          validationRules={{
            required: {
              value: true,
              message: "Email erforderlich",
            },
            pattern: {
              value:
                /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              message: "Ungültiges Format. Bitte gib eine gültige E-Mail-Adresse ein",
            },
          }}
          autoComplete="off"
        />
        <Input
          label="Passwort"
          name="pass"
          type="password"
          placeholder="Gib dein Passwort ein"
          register={register}
          errors={errors.pass}
          touched={!!touchedFields.pass}
          validationRules={{
            required: {
              value: true,
              message: "Passwort erforderlich",
            },
            minLength: {
              value: 8,
              message: "Passwort zu kurz (mind. 8 Zeichen)",
            },
          }}
          autoComplete="off"
        />
      </div>
      <div className="qr-landing__login-footer">
        <Button submit disabled={!isValid || loading} btnClass="ultra-insider">
          Einloggen
        </Button>
      </div>
    </form>
  );
}

export default function QrLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState("choice"); // 'choice' | 'login' | 'consent'
  const [m2Token, setM2Token] = useState(null);

  const goToRegister = () => navigate("/qr-register/" + code);

  const sessionStartedRef = useRef(false);

  useEffect(() => {
    if (!code) {
      NiceModal.show("qr-error-modal", { type: "error_broken_link" });
      return;
    }

    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;

    const isFromEcom =
      new URLSearchParams(window.location.search).get("er") === "true";

    const onSession = () => {
      if (!localStorage.getItem("token")) {
        trackWebEvent("not_logged_in", { code });
      }
    };

    const finalize = () => {
      if (localStorage.getItem("token")) {
        sessionStorage.setItem(
          "loginSource",
          isFromEcom ? "back_from_ecom" : "already_logged_in"
        );
        goToRegister();
      }
    };

    if (sessionStorage.getItem("scanMethod") === "app_scanner") {
      finalize();
    } else {
      startWebSession(onSession).finally(finalize);
    }
  }, [code, navigate]);

  const vuseRegisterUrl = `${import.meta.env.VITE_VUSE_URL}/create-account?qrv=${code}`;

  return (
    <SideBySideLayout bgImageMobile={BGMobile} bgImageDesktop={BGDesktop} position="center">
      <div className="qr-landing">
        {view === "choice" && (
          <div className="qr-landing__choice">
            <p className="qr-landing__desc">
              Melde dich beim Inspiration Club an,<br />
              um deinen Code zu registrieren und<br />
              weitere Vorteile zu entdecken.
            </p>
            <div className="qr-landing__actions">
              <div className="qr-landing__action-group">
                <p className="qr-landing__label">Ich habe schon ein vuse.com&#8209;Konto</p>
                <Button
                  action={() => {
                    trackWebEvent("click_login", { code });
                    setView("login");
                  }}
                  btnClass="outline"
                >
                  Einloggen
                </Button>
              </div>
              <div className="qr-landing__action-group">
                <p className="qr-landing__label">Ich bin neu hier</p>
                <Button
                  href={vuseRegisterUrl}
                  target="_self"
                  btnClass="ultra-insider"
                  action={() => trackWebEvent("click_register", { code })}
                >
                  Registrieren
                </Button>
              </div>
            </div>
          </div>
        )}

        {view === "login" && (
          <LoginView
            code={code}
            onSuccess={goToRegister}
            onJoin={(token) => {
              setM2Token(token);
              setView("consent");
            }}
          />
        )}

        {view === "consent" && (
          <ConsentView code={code} m2Token={m2Token} onSuccess={goToRegister} />
        )}
      </div>
    </SideBySideLayout>
  );
}
